import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Newspaper, Gavel, Scale, CalendarDays, BadgeCheck,
  Filter, Sparkles, TrendingUp, ListChecks, LogInIcon, Search, Send,
  LayoutGrid, Radio, Folder, Settings, Building2, ChevronDown,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
// import BetaWelcomeModal from "@/components/BetaWelcomeModal";
import WaitlistModal from "@/components/WaitlistModal";
import LandingAssistant from "@/components/LandingAssistant";
import DemoRequestModal from "@/components/DemoRequestModal";

// Scroll-reveal wrapper (respects prefers-reduced-motion via motion-reduce:*).
function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: shown ? `${delay}ms` : "0ms" }}
      className={`transition-all duration-700 ease-out motion-reduce:transition-none motion-reduce:transform-none motion-reduce:opacity-100 ${shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}
    >
      {children}
    </div>
  );
}

// Count-up number that animates once on scroll-in (reduced-motion safe).
function CountUp({ to, className = "" }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setVal(to); return undefined; }
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const dur = 1100, start = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        setVal(Math.round(to * eased));
        if (p < 1) requestAnimationFrame(tick); else setVal(to);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [to]);
  return <span ref={ref} className={className}>{val}</span>;
}

const TREND_DATA = {
  solar: {
    label: "Solar Policy",
    range: "Renewable energy · Last 30 days",
    coverage: [
      { d: "Aug 18", all: 6, high: 1 }, { d: "Aug 23", all: 9, high: 2 },
      { d: "Aug 28", all: 7, high: 2 }, { d: "Sep 02", all: 12, high: 3 },
      { d: "Sep 07", all: 10, high: 3 }, { d: "Sep 12", all: 18, high: 6 },
      { d: "Sep 18", all: 24, high: 9 },
    ],
    keywords: [
      { t: "Open access", v: 92 }, { t: "Additional surcharge", v: 74 },
      { t: "Gujarat solar", v: 61 }, { t: "Banking changes", v: 43 },
    ],
    entities: [
      { t: "GERC", v: 31 }, { t: "GUVNL", v: 22 }, { t: "MGVCL", v: 15 }, { t: "SECI", v: 11 },
    ],
    sources: ["SolarQuarter", "Mercom India", "PV Magazine", "Economic Times", "BusinessLine", "GERC"],
  },
  corridor: {
    label: "EU–India corridor",
    range: "Automotive · Market entry · Last 30 days",
    coverage: [
      { d: "Aug 18", all: 11, high: 2 }, { d: "Aug 23", all: 8, high: 2 },
      { d: "Aug 28", all: 14, high: 4 }, { d: "Sep 02", all: 12, high: 3 },
      { d: "Sep 07", all: 19, high: 5 }, { d: "Sep 12", all: 16, high: 6 },
      { d: "Sep 18", all: 27, high: 8 },
    ],
    keywords: [
      { t: "Free trade agreement", v: 88 }, { t: "EV localisation", v: 69 },
      { t: "Import tariffs", v: 57 }, { t: "Component sourcing", v: 40 },
    ],
    entities: [
      { t: "European Commission", v: 28 }, { t: "MHI", v: 19 }, { t: "SIAM", v: 14 }, { t: "Tata Motors", v: 12 },
    ],
    sources: ["Reuters", "Autocar Pro", "Economic Times", "Politico EU", "BusinessLine", "Mint"],
  },
};

export default function Landing() {
  // const [showBetaModal, setShowBetaModal] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [trendKey, setTrendKey] = useState("solar");
  const trend = TREND_DATA[trendKey];

  // useEffect(() => {
  //   const timer = setTimeout(() => setShowBetaModal(true), 500);
  //   return () => clearTimeout(timer);
  // }, []);

  // const openDemo = () => setShowWaitlist(true);
  const openDemo = () => setShowDemoModal(true);

  return (
    <div className="min-h-screen bg-white text-[#0F1F3D] relative overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="/logo.png" alt="LaunchRadar" className="w-[180px]" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#475569]">
            <a href="#trends" className="hover:text-[#0F1F3D]">Trends</a>
            <a href="#analysis" className="hover:text-[#0F1F3D]">Analysis</a>
            <a href="#coverage" className="hover:text-[#0F1F3D]">Coverage</a>
            <a href="#how" className="hover:text-[#0F1F3D]">How it works</a>
            <Link to="/contact" data-testid="header-contact-link" className="hover:text-[#0F1F3D]">Contact</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="header-login-link" className="text-sm text-[#475569] hover:text-[#0F1F3D]">Sign in</Link>
            <button type="button" data-testid="header-signup-cta" onClick={openDemo}
              className="px-4 py-2 rounded-md bg-[#0F1F3D] text-white text-sm font-medium hover:bg-[#172A4F]">
              Request a demo
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-28 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E2E8F0] bg-white text-xs text-[#475569] mb-7">
            <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] animate-pulse" /> Daily intelligence for your organisation
          </div>
          <h1 className="font-display font-semibold" style={{ fontSize: "clamp(2.25rem,4.2vw,3.5rem)", lineHeight: 1.08, letterSpacing: "-0.02em" }}>
            Know what matters to your project.
          </h1>
          <p className="mt-6 text-lg text-[#475569] max-w-xl leading-relaxed">
            LaunchRadar turns daily news into project-specific intelligence. See what changed, why it matters, the trends taking shape and where your team should focus.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button type="button" data-testid="hero-signup-cta" onClick={openDemo}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#0F1F3D] text-white font-medium hover:bg-[#172A4F]">
              Request a demo <ArrowRight className="h-4 w-4" />
            </button>
            <a href="#analysis" data-testid="hero-explore-link"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-[#0F1F3D] text-[#0F1F3D] hover:bg-[#F8F9FA]">
              Explore the analysis <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </Reveal>

        {/* Dashboard preview */}
        <Reveal delay={120} className="lg:col-span-6">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#0F1F3D]/8 to-[#10B981]/10 rounded-3xl blur-2xl" />
            <div className="relative bg-white border border-[#E2E8F0] rounded-2xl shadow-[0_30px_80px_-20px_rgba(15,31,61,0.2)] overflow-hidden flex">
              {/* mini nav rail */}
              <div className="hidden sm:flex w-12 shrink-0 flex-col items-center gap-3.5 py-4 border-r border-[#EEF2F7]">
                <span className="text-[#0F1F3D]"><ArrowUpRight className="h-5 w-5" /></span>
                <span className="h-8 w-8 rounded-lg bg-[#0F1F3D] text-white flex items-center justify-center"><LayoutGrid className="h-4 w-4" /></span>
                {[Radio, Gavel, TrendingUp, Sparkles, Building2, Folder, Settings].map((Ic, i) => (
                  <span key={i} className="text-[#94A3B8]"><Ic className="h-4 w-4" /></span>
                ))}
              </div>

              {/* main */}
              <div className="flex-1 min-w-0 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.22em] text-[#64748B] font-bold">Today&apos;s intelligence</div>
                    <div className="text-2xl font-bold text-[#0F1F3D] leading-tight mt-0.5">Dashboard</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="px-2.5 py-1.5 rounded-md border border-[#E2E8F0] text-[11px] font-medium text-[#475569]">Open feed →</span>
                    <span className="px-2.5 py-1.5 rounded-md bg-[#0F1F3D] text-white text-[11px] font-medium">New project</span>
                  </div>
                </div>

                {/* stat cards */}
                <div className="grid grid-cols-4 gap-2 mt-4">
                  {[["Active projects", 2], ["Total results", 241], ["High alerts", 13], ["Strategic recs", 12]].map(([l, v]) => (
                    <div key={l} className="rounded-lg border border-[#E2E8F0] p-2.5">
                      <div className="text-[8px] uppercase tracking-wide text-[#94A3B8] font-bold leading-tight">{l}</div>
                      <CountUp to={v} className="block text-xl font-bold text-[#0F1F3D] mt-1 tabular-nums" />
                    </div>
                  ))}
                </div>

                {/* two-column */}
                <div className="grid grid-cols-5 gap-3 mt-3">
                  {/* project summary */}
                  <div className="col-span-3 rounded-lg border border-[#E2E8F0] overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2.5">
                      <span className="text-[13px] font-bold text-[#0F1F3D]">Project summary</span>
                      <span className="px-2 py-0.5 rounded-md bg-[#F1F5F9] text-[#64748B] text-[9px] font-semibold">Sample projects</span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto_auto] gap-x-2 px-3 pb-1 text-[8px] uppercase tracking-wide text-[#94A3B8] font-bold">
                      <span>Project</span><span>Template</span><span className="text-right">Results</span>
                    </div>
                    {[
                      { n: "EU–India corridor", s: "Automotive · Market entry", r: 143 },
                      { n: "Solar Policy", s: "Renewable energy · Open access", r: 98 },
                    ].map((p, i) => (
                      <div key={p.n} className={`grid grid-cols-[1fr_auto_auto] items-center gap-x-2 px-3 py-2.5 ${i === 0 ? "border-t border-[#F1F5F9]" : "border-t border-[#F1F5F9]"}`}>
                        <div className="min-w-0">
                          <div className="text-[12px] font-semibold text-[#0F1F3D] truncate">{p.n}</div>
                          <div className="text-[10px] text-[#94A3B8] truncate">{p.s}</div>
                        </div>
                        <span className="px-1.5 py-0.5 rounded bg-[#ECFDF5] text-[#047857] text-[9px] font-semibold">News</span>
                        <span className="text-[13px] font-bold text-[#0F1F3D] tabular-nums text-right">{p.r}</span>
                      </div>
                    ))}
                  </div>

                  {/* strategic recommendations */}
                  <div className="col-span-2 rounded-lg border border-[#E2E8F0] p-3">
                    <div className="text-[8px] uppercase tracking-[0.14em] text-[#64748B] font-bold leading-tight">Strategic recommendations</div>
                    <div className="text-[12px] text-[#475569] mt-0.5 mb-2">12 to review</div>
                    <div className="flex items-center gap-1.5">
                      <span className="px-1.5 py-0.5 rounded bg-[#ECFDF5] text-[#047857] text-[9px] font-semibold">News</span>
                      <span className="px-1.5 py-0.5 rounded border border-[#FCA5A5] text-[#B91C1C] text-[10px] font-bold tabular-nums">82</span>
                    </div>
                    <div className="text-[12px] font-semibold text-[#0F1F3D] leading-snug mt-1.5">GERC sets INR 0.99/kWh additional surcharge</div>
                    <p className="text-[10.5px] text-[#64748B] leading-snug mt-1">Update the Gujarat solar open access ROI model and revise active client feasibility assumptions.</p>
                    <div className="flex items-center justify-between mt-2 text-[10px]">
                      <span className="text-[#94A3B8]">SolarQuarter</span>
                      <span className="text-[#0F1F3D] font-semibold">View action →</span>
                    </div>
                  </div>
                </div>

                {/* footer */}
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[10px] text-[#94A3B8] truncate">Example workspace · Your projects, sectors and priorities</span>
                  <span className="px-2.5 py-1 rounded-md border border-[#10B981]/40 bg-[#ECFDF5] text-[#047857] text-[10px] font-semibold shrink-0">Project intelligence</span>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Project-specific intelligence */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <Reveal>
          <div className="text-xs uppercase tracking-wide text-[#64748B] font-bold">Project intelligence</div>
          <h2 className="font-display font-semibold mt-3" style={{ fontSize: "clamp(1.6rem,2.8vw,2.25rem)", lineHeight: 1.15, letterSpacing: "-0.02em" }}>
            Every project gets its own intelligence.
          </h2>
          <p className="mt-4 text-[#475569] max-w-2xl leading-relaxed">
            LaunchRadar searches for relevant developments within each project, then scores and explains every result against that project&apos;s goals. Recommendations appear only when an update has a meaningful impact.
          </p>
        </Reveal>

        <Reveal delay={100} className="mt-10">
          <div className="rounded-2xl border border-[#E2E8F0] bg-white shadow-[0_20px_60px_-24px_rgba(15,31,61,0.18)] overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex flex-wrap items-center gap-2">
              <span className="text-sm font-bold text-[#0F1F3D]">Solar Policy</span>
              <span className="px-2 py-0.5 rounded-md bg-[#F1F5F9] text-[#475569] text-[11px] font-medium">Renewable energy</span>
              <span
                data-testid="pi-ask-ai"
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#0F1F3D]/15 bg-white text-[12px] font-semibold text-[#0F1F3D] hover:border-[#0F1F3D] hover:bg-[#F8FAFC] motion-safe:transition-colors cursor-default select-none"
              >
                <Sparkles className="h-3.5 w-3.5 text-[#10B981]" /> Ask AI
              </span>
            </div>
            <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-3">
                <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-bold">Relevance</div>
                <CountUp to={82} className="mt-1 block font-mono-tab text-4xl font-bold text-[#B91C1C]" />
                <div className="mt-3 text-[11px] uppercase tracking-wide text-[#94A3B8] font-bold">Source</div>
                <span
                  role="link"
                  className="inline-block mt-1 text-[13px] text-[#0F1F3D] font-medium underline decoration-[#CBD5E1] underline-offset-2 cursor-pointer hover:decoration-[#0F1F3D] transition-colors"
                >
                  SolarQuarter
                </span>
              </div>
              <div className="lg:col-span-9 space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-bold">Summary</div>
                  <p className="mt-1.5 text-[15px] text-[#0F1F3D] leading-relaxed">
                    GERC introduced an additional surcharge affecting Gujarat open-access consumers, changing the landed cost of solar power and the economics of banking arrangements for commercial projects.
                  </p>
                </div>
                <div className="border-l-4 border-[#B91C1C] pl-4">
                  <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-bold">Why this is relevant</div>
                  <p className="mt-1.5 text-[14px] text-[#334155] leading-relaxed">
                    It directly changes the feasibility model your team maintains for Gujarat rooftop and open-access clients.
                  </p>
                </div>
                <div className="rounded-md border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-[#B45309] font-bold">
                    <Sparkles className="h-3 w-3" /> Strategic recommendation
                  </div>
                  <p className="mt-1.5 text-[14px] text-[#0F1F3D]">Recalculate the open-access RCI model with the new surcharge and flag affected client proposals.</p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Trends */}
      <section id="trends" className="bg-[#F8F9FA] border-y border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <Reveal>
            <div className="text-xs uppercase tracking-wide text-[#64748B] font-bold">Trends &amp; signals</div>
            <h2 className="font-display font-semibold mt-3" style={{ fontSize: "clamp(1.6rem,2.8vw,2.25rem)", lineHeight: 1.15, letterSpacing: "-0.02em" }}>
              See the pattern, not just the stories.
            </h2>
            <p className="mt-4 text-[#475569] max-w-2xl leading-relaxed">
              Understand how attention is shifting across your project. Track coverage volume, rising keywords, frequently mentioned entities and the sources shaping the conversation.
            </p>
          </Reveal>

          <Reveal delay={100} className="mt-10">
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
              {/* project selector */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-wide text-[#94A3B8] font-bold">Project</span>
                  <div className="relative">
                    <select
                      value={trendKey}
                      onChange={(e) => setTrendKey(e.target.value)}
                      data-testid="trends-project-select"
                      className="appearance-none pl-3.5 pr-9 py-2 rounded-lg border border-[#CBD5E1] bg-white text-[13px] font-semibold text-[#0F1F3D] focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20 cursor-pointer hover:border-[#0F1F3D] motion-safe:transition-colors"
                    >
                      {Object.entries(TREND_DATA).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="h-4 w-4 text-[#64748B] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
                <span className="text-xs text-[#94A3B8]">{trend.range}</span>
              </div>

              <div key={trendKey} className="motion-safe:animate-in motion-safe:fade-in-50 motion-safe:duration-500">
                <div className="text-sm font-bold text-[#0F1F3D] mb-4">Coverage volume</div>
                <div style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer>
                    <LineChart data={trend.coverage} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
                      <XAxis dataKey="d" tick={{ fontSize: 11, fill: "#94A3B8" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="all" name="All items" stroke="#0F1F3D" strokeWidth={2} dot={false} isAnimationActive animationDuration={1200} />
                      <Line type="monotone" dataKey="high" name="High priority" stroke="#EF4444" strokeWidth={2} dot={false} isAnimationActive animationDuration={1400} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-bold mb-3">Rising keywords</div>
                    <div className="space-y-2.5">
                      {trend.keywords.map((k) => (
                        <div key={k.t} className="flex items-center gap-3">
                          <div className="w-32 truncate text-[13px] text-[#334155] font-medium">{k.t}</div>
                          <div className="flex-1 h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
                            <div className="h-full rounded-full bg-[#0F1F3D] motion-safe:transition-all motion-safe:duration-700" style={{ width: `${k.v}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-bold mb-3">Most-mentioned entities</div>
                    <div className="space-y-2.5">
                      {trend.entities.map((e) => {
                        const max = Math.max(...trend.entities.map((x) => x.v), 1);
                        const pct = Math.round((e.v / max) * 100);
                        return (
                          <div key={e.t} className="flex items-center gap-3">
                            <div className="w-28 truncate text-[13px] text-[#334155] font-medium" title={e.t}>{e.t}</div>
                            <div className="flex-1 h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
                              <div className="h-full rounded-full bg-[#10B981] motion-safe:transition-all motion-safe:duration-700" style={{ width: `${Math.max(pct, 8)}%` }} />
                            </div>
                            <span className="w-6 text-right text-[12px] font-bold text-[#0F1F3D] tabular-nums">{e.v}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-bold mb-3">Top sources</div>
                    <div className="flex flex-wrap gap-2">
                      {trend.sources.map((s) => (
                        <span key={s} className="px-2.5 py-1 rounded-full bg-[#F1F5F9] text-[12px] text-[#334155]">{s}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Analysis */}
      <section id="analysis" className="max-w-6xl mx-auto px-6 py-20">
        <Reveal>
          <div className="text-xs uppercase tracking-wide text-[#64748B] font-bold">Analysis</div>
          <h2 className="font-display font-semibold mt-3" style={{ fontSize: "clamp(1.6rem,2.8vw,2.25rem)", lineHeight: 1.15, letterSpacing: "-0.02em" }}>
            Ask questions that move your work forward.
          </h2>
          <p className="mt-4 text-[#475569] max-w-2xl leading-relaxed">
            Use AI to explore your collected intelligence in the context of your organisation and projects. Understand what changed, compare business implications and build a brief your team can use, with sources you can check.
          </p>
        </Reveal>

        <Reveal delay={100} className="mt-10">
          <div className="rounded-2xl border border-[#E2E8F0] bg-white shadow-[0_20px_60px_-24px_rgba(15,31,61,0.18)] overflow-hidden grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-[#E2E8F0] p-5 bg-[#F8FAFC]">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-[#0F1F3D]">Analysis</span>
              </div>
              <button type="button" className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-[#0F1F3D] text-[#0F1F3D] text-sm font-semibold">
                + New conversation
              </button>
              <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-bold mt-5 mb-2">Recent conversations</div>
              <div className="space-y-1.5">
                {["GERC surcharge impact", "What should our team update?", "Weekly Solar Policy brief"].map((c, i) => (
                  <div key={c} className={`rounded-lg px-3 py-2 text-[13px] ${i === 0 ? "bg-[#EEF4FF] text-[#0F1F3D] font-medium" : "text-[#475569]"}`}>{c}</div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-8 p-5 flex flex-col">
              <Reveal className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[#0F1F3D] text-white px-4 py-2.5 text-sm">
                  How does the new GERC surcharge affect our Gujarat solar open-access work?
                </div>
              </Reveal>
              <Reveal delay={180} className="mt-3">
                <div className="rounded-2xl rounded-bl-sm bg-[#F8FAFC] border border-[#E2E8F0] px-4 py-3 text-sm text-[#1E293B]">
                  <p className="font-semibold text-[#0F1F3D]">Impact on Gujarat solar open access</p>
                  <p className="mt-1.5 leading-relaxed">
                    The new surcharge raises the landed cost for open-access consumers and changes banking economics. Recommended next steps: recalculate project economics and review affected client proposals.
                  </p>
                  <div className="mt-3 pt-2 border-t border-[#E2E8F0] text-[10px] uppercase tracking-wide text-[#94A3B8] font-bold">Sources</div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {["GERC surcharge report", "Mercom India"].map((s) => (
                      <span key={s} className="inline-flex items-center gap-1 text-xs text-[#0F1F3D]"><Search className="h-3 w-3" />{s}</span>
                    ))}
                  </div>
                </div>
              </Reveal>
              <Reveal delay={340} className="mt-3 flex flex-wrap gap-2">
                {["What does this mean for our work?", "What should our team review?", "What is still uncertain?"].map((q) => (
                  <span key={q} className="px-3 py-1.5 rounded-full border border-[#CBD5E1] text-xs text-[#334155] hover:border-[#0F1F3D] hover:text-[#0F1F3D] motion-safe:transition-colors cursor-default">{q}</span>
                ))}
              </Reveal>
              <Reveal delay={460} className="mt-4 flex items-center gap-2">
                <div className="flex-1 px-4 py-2.5 rounded-full border border-[#CBD5E1] text-sm text-[#94A3B8]">Ask a question about your project…</div>
                <span className="h-10 w-10 flex items-center justify-center rounded-full bg-[#0F1F3D] text-white motion-safe:transition-transform hover:scale-105"><Send className="h-4 w-4" /></span>
              </Reveal>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Coverage */}
      <section id="coverage" className="bg-[#F8F9FA] border-y border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <Reveal>
            <div className="text-xs uppercase tracking-wide text-[#64748B] font-bold">Coverage</div>
            <h2 className="font-display font-semibold mt-3" style={{ fontSize: "clamp(1.6rem,2.8vw,2.25rem)", lineHeight: 1.15, letterSpacing: "-0.02em" }}>
              One view of the developments shaping your project.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Newspaper, tag: "Articles and market intelligence", title: "News intelligence", live: true, desc: "Continuously analyse relevant reporting to identify important developments, changing narratives and implications for your project." },
              { icon: Gavel, tag: "Bids and procurement", title: "Tender intelligence", soon: true, desc: "Identify relevant opportunities, understand deadlines and surface strategic recommendations when an opportunity warrants attention." },
              { icon: Scale, tag: "Policy and regulatory updates", title: "Regulatory intelligence", soon: true, desc: "Understand policy changes, the organisations affected and what each development could mean for your project." },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 90}>
                <div className={`h-full border rounded-xl p-6 ${c.soon ? "border-[#E2E8F0] bg-white/60" : "border-[#E2E8F0] bg-white hover:border-[#0F1F3D] transition-colors"}`}>
                  <div className="flex items-center justify-between h-6">
                    <div className="h-11 w-11 rounded-md flex items-center justify-center bg-[#0F1F3D]/8 text-[#0F1F3D]"><c.icon className="h-5 w-5" /></div>
                    {c.live && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                        <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span> Live
                      </span>
                    )}
                    {c.soon && <span className="text-xs text-[#64748B] bg-[#F1F5F9] border border-[#E2E8F0] px-2 py-1 rounded-md">Coming soon</span>}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-bold mt-5">{c.tag}</div>
                  <h3 className="text-lg font-bold text-[#0F1F3D] mt-1">{c.title}</h3>
                  <p className="text-[14px] text-[#475569] mt-2 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section id="why" className="max-w-6xl mx-auto px-6 py-20">
        <Reveal>
          <div className="text-xs uppercase tracking-wide text-[#64748B] font-bold">Why LaunchRadar</div>
          <h2 className="font-display font-semibold mt-3" style={{ fontSize: "clamp(1.6rem,2.8vw,2.25rem)", lineHeight: 1.15, letterSpacing: "-0.02em" }}>
            Your daily intelligence. Everything in one place.
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: CalendarDays, t: "Runs every day", d: "Set up your organisation and project once. LaunchRadar continuously brings relevant developments into your project intelligence." },
            { icon: BadgeCheck, t: "Analyses against your project", d: "Each update receives a relevance score and a clear explanation of how it connects to your organisation, goals and decisions." },
            { icon: Filter, t: "Reduces repeated coverage", d: "Duplicate links and repeated stories are consolidated, helping you focus on developments rather than sift through noise." },
            { icon: Sparkles, t: "Strategic recommendations", d: "Get a recommended next step when a development has a meaningful impact on your organisation and its projects." },
            { icon: TrendingUp, t: "Explore your intelligence", d: "Ask for a weekly summary, compare developments or investigate a question, with citations back to the underlying sources." },
            { icon: ListChecks, t: "Track trends and organisations", d: "See coverage momentum, rising topics and the institutions or competitors appearing across your project." },
          ].map((w, i) => (
            <Reveal key={w.t} delay={(i % 3) * 80}>
              <div className="h-full border border-[#E2E8F0] rounded-xl p-6 bg-white">
                <w.icon className="h-5 w-5 text-[#0F1F3D]" />
                <div className="text-base font-bold text-[#0F1F3D] mt-4">{w.t}</div>
                <div className="text-[14px] text-[#475569] mt-2 leading-relaxed">{w.d}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-[#F8F9FA] border-y border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <Reveal>
            <div className="text-xs uppercase tracking-wide text-[#64748B] font-bold">How it works</div>
            <h2 className="font-display font-semibold mt-3" style={{ fontSize: "clamp(1.6rem,2.8vw,2.25rem)", lineHeight: 1.15, letterSpacing: "-0.02em" }}>
              Set up your project once. Build intelligence every day.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Add your context", d: "Describe your organisation, project, goals, sectors and the organisations or sources you want to understand." },
              { n: "02", t: "LaunchRadar analyses continuously", d: "Relevant developments are consolidated, scored and interpreted against what matters to your project." },
              { n: "03", t: "Understand and act", d: "Review significant developments, emerging trends, project implications and strategic recommendations — then analyse and interact with your data to build briefs and summaries you can share with your team." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 100}>
                <div className="h-full bg-white border border-[#E2E8F0] rounded-xl p-6">
                  <div className="font-mono-tab text-sm font-bold text-[#10B981]">{s.n}</div>
                  <div className="text-lg font-bold text-[#0F1F3D] mt-3">{s.t}</div>
                  <div className="text-[14px] text-[#475569] mt-2 leading-relaxed">{s.d}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-6 py-24 text-center">
        <Reveal>
          <h2 className="font-display font-semibold" style={{ fontSize: "clamp(1.8rem,3vw,2.5rem)", lineHeight: 1.1, letterSpacing: "-0.02em" }}>
            See what it can do for your business.
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button type="button" data-testid="cta-signup" onClick={openDemo}
              className="inline-flex h-11 min-w-[180px] items-center justify-center gap-2 rounded-md bg-[#0F1F3D] px-6 font-medium text-white hover:bg-[#172A4F]">
              Request a demo <ArrowRight className="h-4 w-4" />
            </button>
            <Link to="/login" data-testid="cta-login"
              className="inline-flex h-11 min-w-[180px] items-center justify-center gap-2 rounded-md border border-[#0F1F3D] px-6 text-[#0F1F3D] hover:bg-[#F8F9FA]">
              Sign in <LogInIcon className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      <footer className="border-t border-[#E2E8F0] py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#64748B]">
          <div>© 2026 LaunchRadar</div>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="hover:text-[#0F1F3D]">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-[#0F1F3D]">Terms &amp; Conditions</Link>
            <Link to="/faq" className="hover:text-[#0F1F3D]">FAQs</Link>
            <Link to="/contact" className="hover:text-[#0F1F3D]">Contact</Link>
          </div>
        </div>
      </footer>

      {/* <BetaWelcomeModal open={showBetaModal} onClose={() => setShowBetaModal(false)} /> */}
      {/* {showWaitlist && <WaitlistModal onClose={() => setShowWaitlist(false)} />} */}
      {showDemoModal && <DemoRequestModal onClose={() => setShowDemoModal(false)} />}

      {/* Floating FAQ assistant */}
      <LandingAssistant onRequestDemo={openDemo} />
    </div>
  );
}